import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { TIDS } from '../constants/testIds';
import PortalDialog from './PortalDialog';

const Navbar = () => {
  const { t, lang, toggle } = useI18n();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      data-testid={TIDS.navbar}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/75 backdrop-blur-2xl backdrop-saturate-150 border-b border-black/5'
          : 'bg-white/40 backdrop-blur-xl'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-14 flex items-center justify-between">
        <button
          data-testid={TIDS.navLogo}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/logo-icon.png"
            alt="Premium Laundry Services logo"
            className="w-9 h-9 object-contain"
          />
          <span className="text-[15px] font-semibold tracking-tight text-[#1D1D1F] group-hover:opacity-80 transition">
            Premium Laundry Services
          </span>
        </button>

        <ul className="hidden md:flex items-center gap-8 text-[13px] font-medium text-[#1D1D1F]/80">
          <li>
            <button
              data-testid={TIDS.navServices}
              onClick={() => scrollTo('services')}
              className="hover:text-[#0071E3] transition"
            >
              {t.nav.services}
            </button>
          </li>
          <li>
            <button
              data-testid={TIDS.navHowItWorks}
              onClick={() => scrollTo('how')}
              className="hover:text-[#0071E3] transition"
            >
              {t.nav.how}
            </button>
          </li>
          <li>
            <button
              data-testid={TIDS.navMemberships}
              onClick={() => scrollTo('memberships')}
              className="hover:text-[#0071E3] transition"
            >
              {t.nav.memberships}
            </button>
          </li>
        </ul>

        <div className="flex items-center gap-3">
          <PortalDialog
            trigger={
              <button
                data-testid="nav-portal-trigger"
                className="hidden sm:inline-block text-[12px] font-medium text-[#1D1D1F]/70 hover:text-[#0071E3] transition px-2 py-1"
              >
                {t.nav.manage}
              </button>
            }
          />
          <button
            data-testid={TIDS.langToggle}
            onClick={toggle}
            className="relative flex items-center text-[12px] font-semibold text-[#1D1D1F]/70 hover:text-[#0071E3] transition px-2 py-1 rounded-full"
            aria-label="Toggle language"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={lang}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.15 }}
                className="uppercase tracking-wider"
              >
                {lang === 'es' ? 'ES · EN' : 'EN · ES'}
              </motion.span>
            </AnimatePresence>
          </button>
          <button
            data-testid={TIDS.navBook}
            onClick={() => scrollTo('booking')}
            className="bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold rounded-full px-4 py-2 transition-transform hover:scale-[1.03] active:scale-95 shadow-sm"
          >
            {t.nav.book}
          </button>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
